import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { getTransactions } from '../../actions/canTransaction';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Form, 
    Button, 
    Table, 
    Badge, 
    Alert, 
    Spinner,
    Toast,
    ToastContainer,
    Pagination
} from 'react-bootstrap';
import "bootstrap/dist/css/bootstrap.min.css";

const Transactions = ({location, transactions, getTransactions, pageList, pages, activePage, loading}) => {

    const initialState = {
        vendingMachineId: '',
        startDate: '',
        endDate: '',
        phone: '',
        transactionId: '',
        latest: true
    };

    const [formData, setFormData] = useState(initialState);
    const [data, setData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [queryString, setQueryString] = useState("page=1&latest=true");
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVariant, setToastVariant] = useState('success');
    const [specificUserId, setSpecificUserId] = useState("");

    const showAlert = (message, variant = 'danger') => {
        setToastMessage(message);
        setToastVariant(variant);
        setShowToast(true);
    };

    useEffect(() => {
        document.title = 'Transactions';
        if(transactions === undefined){
            let query = queryString;
            if(location && location.search){
                let phoneNumber = location.search.split("=");
                if(phoneNumber.length === 2 && phoneNumber[1].length === 10){
                    query += "&phone=" + phoneNumber[1];
                    setSpecificUserId(phoneNumber[1]);
                }
            }
            getTransactions(query);
        } else {
            processTransactionData(transactions);
            setCurrentPage(activePage || 1);
        }
    }, [location, transactions, getTransactions]);

    const processTransactionData = (transactionData) => {
        const processedData = transactionData.map(transaction => ({
            status: transaction.status,
            returnCan: transaction.returnCan ? "Yes" : "No",
            _id: transaction._id,
            containerOpen: transaction.containerOpen ? "Yes" : "No",
            containerId: transaction.containerId || 'N/A',
            transactionId: transaction.transactionId,
            time: new Date(transaction.time).toLocaleString(),
            rawTime: new Date(transaction.time),
            brand: transaction.product?.brand || 'Unknown',
            brandId: transaction.product?._id || 'N/A',
            vendingMachineId: transaction.vendingMachine?._id || 'N/A',
            vendingMachineName: transaction.vendingMachine?.name || 'Deleted',
            product: transaction.brand || 'Unknown',
            userName: transaction.user ? transaction.user.name : "Deleted User",
            userId: transaction.user ? transaction.user._id : "deleted",
            phone: transaction.user ? transaction.user.phone : "N/A",
        }));

        // Sort by latest if needed
        if (formData.latest) {
            processedData.sort((a, b) => b.rawTime - a.rawTime);
        } else {
            processedData.sort((a, b) => a.rawTime - b.rawTime);
        }

        setData(processedData);
    };




    const {
        vendingMachineId,
        startDate,
        endDate,
        phone,
        transactionId,
        latest
    } = formData;

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    }

    const onSubmit = e => {
        e.preventDefault();
        
        if (formData.startDate && formData.endDate) {
            const startDate = new Date(formData.startDate).getTime();
            const endDate = new Date(formData.endDate).getTime();
            if(endDate < startDate){
                showAlert("End date must be after start date");
                return;
            }
        }
        
        // Build query string for filters
        let newQueryString = `page=1&latest=${formData.latest}`;
        if(formData.startDate && formData.endDate){
            newQueryString += `&startDate=${formData.startDate}&endDate=${formData.endDate}`;
        }
        if(formData.vendingMachineId){
            newQueryString += `&vendingMachine=${formData.vendingMachineId}`;
        }
        if(formData.transactionId){
            newQueryString += `&transactionId=${formData.transactionId}`;
        }
        if(formData.phone){
            newQueryString += `&phone=${formData.phone}`;
        }
        
        setQueryString(newQueryString);
        setCurrentPage(1);
        getTransactions(newQueryString);
    };

    const handleSort = (field) => {
        const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortDirection(direction);
        
        const sortedData = [...data].sort((a, b) => {
            if (direction === 'asc') {
                return a[field] > b[field] ? 1 : -1;
            }
            return a[field] < b[field] ? 1 : -1;
        });
        setData(sortedData);
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return <i className="fas fa-sort text-muted ms-1"></i>;
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up text-primary ms-1"></i>
            : <i className="fas fa-sort-down text-primary ms-1"></i>;
    };

    const clearFilters = () => {
        setFormData(initialState);
        setSpecificUserId("");
        const newQueryString = "page=1&latest=true";
        setQueryString(newQueryString);
        setCurrentPage(1);
        getTransactions(newQueryString);
        showAlert("Filters cleared successfully!", "info");
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > pages) return;
        
        const newQueryString = queryString.replace(`page=${currentPage}`, `page=${page}`);
        setQueryString(newQueryString);
        setCurrentPage(page);
        getTransactions(newQueryString);
    };

    const renderPagination = () => {
        // If no pages data from backend, don't show pagination
        if (!pages || pages <= 1) return null;

        const items = [];
        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(pages, startPage + maxVisiblePages - 1);

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" />);
            }
        }

        // Page numbers
        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < pages) {
            if (endPage < pages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" />);
            }
            items.push(
                <Pagination.Item key={pages} onClick={() => handlePageChange(pages)}>
                    {pages}
                </Pagination.Item>
            );
        }

        return (
            <div className="p-3 border-top bg-light">
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.Prev
                            disabled={currentPage === 1}
                            onClick={() => handlePageChange(currentPage - 1)}
                        />
                        {items}
                        <Pagination.Next
                            disabled={currentPage === pages}
                            onClick={() => handlePageChange(currentPage + 1)}
                        />
                    </Pagination>
                    <div className="ms-3 d-flex align-items-center">
                        <small className="text-muted">
                            Page {currentPage} of {pages} {pages > 1 ? `(${data.length} records)` : ''}
                        </small>
                    </div>
                </div>
            </div>
        );
    };



    const getStatusBadge = (status) => {
        switch(status?.toLowerCase()) {
            case 'opened':
                return <Badge bg="success">Opened</Badge>;
            case 'sent':
                return <Badge bg="warning">Sent</Badge>;
            case 'failed':
                return <Badge bg="danger">Failed</Badge>;
            case 'nostock':
                return <Badge bg="secondary">No Stock</Badge>;
            default:
                return <Badge bg="info">{status || 'Unknown'}</Badge>;
        }
    };

    const renderTransactionTable = () => {
        if (!data || data.length === 0) {
            return (
                <Alert variant="info" className="text-center">
                    <i className="fas fa-info-circle me-2"></i>
                    No transaction data found. Try adjusting your filters.
                </Alert>
            );
        }

        return (
            <div className="table-responsive">
                <Table striped bordered hover className="mb-0">
                    <thead className="table-dark">
                        <tr>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('transactionId')}
                            >
                                Transaction ID
                                {getSortIcon('transactionId')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('userName')}
                            >
                                User
                                {getSortIcon('userName')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('phone')}
                            >
                                Phone
                                {getSortIcon('phone')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('vendingMachineName')}
                            >
                                Vending Machine
                                {getSortIcon('vendingMachineName')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('product')}
                            >
                                Product
                                {getSortIcon('product')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('containerId')}
                            >
                                Container ID
                                {getSortIcon('containerId')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('status')}
                            >
                                Status
                                {getSortIcon('status')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('returnCan')}
                            >
                                Return Can
                                {getSortIcon('returnCan')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('containerOpen')}
                            >
                                Container Open
                                {getSortIcon('containerOpen')}
                            </th>
                            <th 
                                className="cursor-pointer user-select-none"
                                onClick={() => handleSort('time')}
                            >
                                Time
                                {getSortIcon('time')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((row, index) => (
                            <tr key={row._id || index}>
                                <td>
                                    <Link 
                                        to={`/inventory/v1.2/transactions/${row._id}`}
                                        className="text-decoration-none fw-bold text-primary transaction-link"
                                        title={`View transaction details: ${row.transactionId}`}
                                    >
                                        <span className="text-truncate d-inline-block" style={{maxWidth: '150px'}}>
                                            {row.transactionId}
                                        </span>
                                    </Link>
                                </td>
                                <td>
                                    <span className={`${row.userName === 'Deleted User' ? 'text-muted fst-italic' : ''}`}>
                                        {row.userName}
                                    </span>
                                </td>
                                <td>
                                    <span className={`${row.phone === 'N/A' ? 'text-muted fst-italic' : ''}`}>
                                        {row.phone}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-truncate d-inline-block" style={{maxWidth: '150px'}} title={row.vendingMachineName}>
                                        {row.vendingMachineName}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-truncate d-inline-block" style={{maxWidth: '120px'}} title={row.product}>
                                        {row.product}
                                    </span>
                                </td>
                                <td>
                                    <span className="text-truncate d-inline-block" style={{maxWidth: '120px'}} title={row.containerId}>
                                        {row.containerId}
                                    </span>
                                </td>
                                <td>
                                    {getStatusBadge(row.status)}
                                </td>
                                <td>
                                    <Badge bg={row.returnCan === 'Yes' ? 'success' : 'danger'}>
                                        {row.returnCan}
                                    </Badge>
                                </td>
                                <td>
                                    <Badge bg={row.containerOpen === 'Yes' ? 'success' : 'danger'}>
                                        {row.containerOpen}
                                    </Badge>
                                </td>
                                <td>
                                    <small className="text-muted">
                                        {new Date(row.rawTime).toLocaleDateString()}<br/>
                                        {new Date(row.rawTime).toLocaleTimeString()}
                                    </small>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <Container fluid className="py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="text-primary mb-0">
                    <i className="fas fa-exchange-alt me-2"></i>
                    Can Transactions
                </h2>
                {specificUserId && (
                    <Badge bg="info">
                        <i className="fas fa-user me-1"></i>
                        Phone Specific View
                    </Badge>
                )}
            </div>

            {/* Filters Card */}
            <Card className="shadow-sm mb-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-filter me-2"></i>
                        Search Filters
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={onSubmit}>
                        <Row className="g-3">
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-cog me-2"></i>
                                        Vending Machine ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="vendingMachineId"
                                        value={vendingMachineId}
                                        onChange={onChange}
                                        placeholder="Enter vending machine ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-receipt me-2"></i>
                                        Transaction ID
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="transactionId"
                                        value={transactionId}
                                        onChange={onChange}
                                        placeholder="Enter transaction ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-phone me-2"></i>
                                        Phone Number
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={phone}
                                        onChange={onChange}
                                        placeholder="Enter phone number"
                                        maxLength={10}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        Start Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={startDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-calendar me-2"></i>
                                        End Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={endDate}
                                        onChange={onChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6} lg={4}>
                                <Form.Group>
                                    <Form.Label className="fw-bold">
                                        <i className="fas fa-sort me-2"></i>
                                        Sort Order
                                    </Form.Label>
                                    <Form.Select
                                        name="latest"
                                        value={latest}
                                        onChange={onChange}
                                    >
                                        <option value="true">Newest First</option>
                                        <option value="false">Oldest First</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2 w-100">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading}
                                        className="flex-fill"
                                    >
                                        {loading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Searching...
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-search me-2"></i>
                                                Apply Filters
                                            </>
                                        )}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline-secondary"
                                        onClick={clearFilters}
                                        disabled={loading}
                                    >
                                        <i className="fas fa-times me-2"></i>
                                        Clear
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* Summary Cards */}
            {data.length > 0 && (
                <Row className="g-3 mb-4 justify-content-center">
                    <Col lg={2} md={4} sm={6}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-receipt text-primary fa-2x mb-2"></i>
                                <h5 className="card-title">Total Records</h5>
                                <h3 className="text-primary">{data.length}</h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-check-circle text-success fa-2x mb-2"></i>
                                <h5 className="card-title">Opened</h5>
                                <h3 className="text-success">
                                    {data.filter(row => row.status?.toLowerCase() === 'opened').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-paper-plane text-warning fa-2x mb-2"></i>
                                <h5 className="card-title">Sent</h5>
                                <h3 className="text-warning">
                                    {data.filter(row => row.status?.toLowerCase() === 'sent').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-times-circle text-danger fa-2x mb-2"></i>
                                <h5 className="card-title">Failed</h5>
                                <h3 className="text-danger">
                                    {data.filter(row => row.status?.toLowerCase() === 'failed').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col lg={2} md={4} sm={6}>
                        <Card className="text-center h-100">
                            <Card.Body>
                                <i className="fas fa-box-open text-secondary fa-2x mb-2"></i>
                                <h5 className="card-title">No Stock</h5>
                                <h3 className="text-secondary">
                                    {data.filter(row => row.status?.toLowerCase() === 'nostock').length}
                                </h3>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Results Card */}
            <Card className="shadow-sm">
                <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                        <i className="fas fa-table me-2"></i>
                        Transaction Records
                    </h5>
                    <div className="d-flex align-items-center gap-3">
                        {pages > 1 && (
                            <small className="text-light">
                                Page {currentPage} of {pages}
                            </small>
                        )}
                        {data.length > 0 && (
                            <Badge bg="light" text="dark">
                                {data.length} record{data.length !== 1 ? 's' : ''}
                            </Badge>
                        )}
                        {/* Debug info - can be removed later */}
                        <small className="text-warning">
                            Pages: {pages || 'undefined'} | Current: {currentPage}
                        </small>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <div className="mt-2">Loading transaction data...</div>
                        </div>
                    ) : (
                        <>
                            {renderTransactionTable()}
                            {/* Pagination */}
                            {renderPagination()}
                            
                            {/* Fallback pagination info when no pagination data from backend */}
                            {!pages && data.length > 0 && (
                                <div className="p-3 border-top bg-light text-center">
                                    <small className="text-muted">
                                        Showing {data.length} records
                                        {data.length >= 50 && " (pagination data not available)"}
                                    </small>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Toast Notifications */}
            <ToastContainer position="top-end" className="p-3">
                <Toast
                    show={showToast}
                    onClose={() => setShowToast(false)}
                    delay={5000}
                    autohide
                    bg={toastVariant}
                >
                    <Toast.Header>
                        <strong className="me-auto">
                            {toastVariant === 'success' ? 'Success' : toastVariant === 'info' ? 'Info' : 'Alert'}
                        </strong>
                    </Toast.Header>
                    <Toast.Body className={toastVariant === 'danger' ? '' : 'text-white'}>
                        {toastMessage}
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            <style>{`
                .cursor-pointer {
                    cursor: pointer;
                }
                .user-select-none {
                    user-select: none;
                }
                .table th:hover {
                    background-color: #495057 !important;
                }
                .transaction-link:hover {
                    text-decoration: underline !important;
                    color: #0d6efd !important;
                }
                .transaction-link:hover span {
                    color: #0d6efd !important;
                }
            `}</style>
        </Container>
    )
}

Transactions.propTypes = {
    getTransactions: PropTypes.func.isRequired,
    transactions: PropTypes.array,
    pages: PropTypes.number,
    activePage: PropTypes.number,
    pageList: PropTypes.array,
    location: PropTypes.object,
    loading: PropTypes.bool
}

const mapStateToProps = state => ({
    transactions: state.canTransaction.transactions,
    pages: state.canTransaction.pages,
    pageList: state.canTransaction.pageList,
    activePage: state.canTransaction.activePage,
    loading: state.canTransaction.loading
})

export default connect( mapStateToProps, { getTransactions } )( Transactions );